import * as React from 'react';
import { useMemo } from 'react';
import {
    RoomPriceComponent,
    ProgramPriceComponent,
} from '@digitalopsdev/serviceops-reservations-components';
import type {
    ClusterKind,
    DayRateSnapshot,
    RateSnapshotData,
} from './types';
import { getClusterCurrentDays } from './clusterUtils';
import {
    buildProgramBookingStateForDialog,
    buildReservationStateForDialog,
    buildRoomBookingStateForDialog,
} from './dialogAdapter';

interface Props {
    cluster: {
        id: string;
        kind: ClusterKind;
        memberIds: string[];
        displayName: string;
    };
    targetCurrent: RateSnapshotData | null;
    selectedDates: Set<string>;
}

interface DialogInputDay {
    dayRate: DayRateSnapshot;
    displayDate: Date;
}

export function CreateClusterTotalPriceCell({ cluster, targetCurrent, selectedDates }: Props) {
    const currentDays = useMemo(
        () => getClusterCurrentDays(cluster.memberIds, cluster.kind, targetCurrent),
        [cluster.memberIds, cluster.kind, targetCurrent],
    );

    const inputs: DialogInputDay[] = useMemo(
        () => currentDays
            .filter(d => selectedDates.has(d.date))
            .map(d => ({ dayRate: d, displayDate: new Date(d.date + 'T00:00:00Z') })),
        [currentDays, selectedDates],
    );

    if (currentDays.length === 0) {
        return <div className="text-xs text-gray-400">No current rates loaded</div>;
    }

    if (inputs.length === 0) {
        return <div className="text-xs text-gray-400">No dates selected</div>;
    }

    const isRoom = cluster.kind === 'ROOM';
    const typeKey = `cluster:${cluster.id}`;
    const reservationState = buildReservationStateForDialog(
        isRoom ? typeKey : null,
        isRoom ? cluster.displayName : null,
        !isRoom ? typeKey : null,
        !isRoom ? cluster.displayName : null,
    );

    return (
        <div className="text-xs flex items-center gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
            <span className="text-gray-500">Current:</span>
            {isRoom
                ? (
                    <RoomPriceComponent
                        reservationState={reservationState}
                        roomBookingState={buildRoomBookingStateForDialog(typeKey, inputs)}
                    />
                )
                : (
                    <ProgramPriceComponent
                        reservationState={reservationState}
                        programBookingState={buildProgramBookingStateForDialog(typeKey, inputs)}
                    />
                )}
        </div>
    );
}
